<template>
  <div>
    <div class="row page-titles">
      <div class="col-md-5 align-self-center">
        <h4 class="text-themecolor">{{ $t('Notification List') }}</h4>
      </div>
      <div class="col-md-7 align-self-center text-right">
        <div class="d-flex justify-content-end align-items-center">
          <breadcrumb :page="currentPage" />
        </div>
      </div>
    </div>
    <loading-container :loading="loading">
      <notification-filter v-model="filter" @click="getNotifications" />
      <div v-if="!(items && items.length)" class="text-center">
        {{ $t('The notification list is empty') }}
      </div>
      <div v-else class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-striped dataTable no-footer">
                  <thead>
                    <tr>
                      <th>{{ $t('Message') }}</th>
                      <th>{{ $t('Type') }}</th>
                      <th>{{ $t('Created Date') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item) in items" :class="!item.status ? 'odd' : 'even'" :key="item.id">
                      <td>
                        <a style="cursor: pointer" @click="handleView(item)">{{ item.message }}</a>
                      </td>
                      <td>
                        {{ $t(type(item.type)) }}
                      </td>
                      <td class="nowrap">{{ item.createdAt | datetimeDisplay }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-center">
          <b-pagination v-model="filter.page" :total-rows="total" :per-page="filter.limit"
            aria-controls="wemb-order-list" :first-text="$t('First')" :prev-text="$t('Prev')" :next-text="$t('Next')"
            :last-text="$t('Last')" />
        </div>
      </div>
    </loading-container>
  </div>
</template>


<script>
import LoadingContainer from "@/components/Misc/LoadingContainer";
import InputSelect from "@/components/Form/InputSelect";
import InputText from "@/components/Form/InputText";
import Modal from "@/components/Misc/Modal";
import LinkButton from "@/components/Misc/LinkButton";
import FormButton from "@/components/Form/FormButton";
import Breadcrumb from "@/components/Common/Breadcrumb";
import TicketStatus from '@/components/Common/TicketStatus';
import TopicLabel from '@/components/Common/TopicLabel'
import UserAvatar from '@/components/Common/UserAvatar'
import NotificationService from "@/Services/NotificationService";
import PageMixin from "@/mixins/PageMixin";
import NotificationHelper from "@/helpers/NotificationHelper";
import NotificationFilter from "@/views/Notification/NotificationFilter";
import Constants from "@/helpers/Constants";
import { listenEventDict } from "@/libs/createWebSocketPlugin";
import Enums from "@/libs/enums";

export default {
  name: "Notification",
  components: {
    LoadingContainer,
    InputSelect,
    Modal,
    LinkButton,
    InputText,
    FormButton,
    Breadcrumb,
    TicketStatus,
    TopicLabel,
    UserAvatar,
    NotificationFilter,
  },
  data() {
    return {
      items: [],
      loading: false,
      totalUnviewed: 0,
      total: 0,
      totalPage: 0,
      /**
       * @type {{
       *  status?: 0 | 1;
       *  key?: string;
       *  limit: number;
       *  page: number;
       *  createdFrom?: Date;
       *  createdTo?: Date;
       * }}
      */
      filter: {
        limit: 10,
        page: 1,
      },
    }
  },
  mixins: [PageMixin],
  beforeMount() {
    this.getNotifications({ ...this.filter });
  },
  mounted() {
    /** @type {import('socket.io-client').Socket} */
    const socket = this.$store.$socket;

    if (
      socket &&
      this.$store.state.auth &&
      this.$store.state.auth.item &&
      this.$store.state.auth.item.user &&
      this.$store.state.auth.item.user.role
    ) {
      const { role } = this.$store.state.auth.item.user;
      switch (role) {
        case Enums.ROLES.admin: {
          [
            listenEventDict["update-order"],
            listenEventDict["request-topup"],
            listenEventDict["view-notification"],
            listenEventDict["request-cancel"],
            listenEventDict["warning-low-stock"],
          ].forEach((event) => {
            socket.on(event, (data) => {
              const isValid = [
                Constants.notificationType["update-order"],
                Constants.notificationType["request-topup"],
                Constants.notificationType["view-notification"],
                Constants.notificationType["request-cancel"],
                Constants.notificationType["warning-low-stock"],
              ].includes(data);

              if (!isValid) {
                return;
              }

              this.getNotifications({
                ...this.filter
              });
            });
          });
          return;
        }
        case Enums.ROLES.seller: {
          [
            listenEventDict["approve-deposit"],
            listenEventDict["approve-refund"],
            listenEventDict["allow-update"],
            listenEventDict["reply-request-cancel"],
            listenEventDict["order-cancelled"],
          ].forEach((event) => {
            socket.on(event, (data) => {
              const isValid = [
                Constants.notificationType["approve-deposit"],
                Constants.notificationType["approve-refund"],
                Constants.notificationType["allow-update"],
                Constants.notificationType["reply-request-cancel"],
                Constants.notificationType["order-cancelled"],
              ].includes(data);

              if (!isValid) {
                return;
              }

              this.getNotifications({
                ...this.filter
              });
              
            });
          });
          return;
        }
        case Enums.ROLES.manager: {
          [
            listenEventDict["update-order"],
            listenEventDict["view-notification"],
            listenEventDict["request-cancel"],
          ].forEach((event) => {
            socket.on(event, (data) => {
              const isValid = [
                Constants.notificationType["update-order"],
                Constants.notificationType["view-notification"],
                Constants.notificationType["request-cancel"],
              ].includes(data);

              if (!isValid) {
                return;
              }

              this.getNotifications({
                ...this.filter
              });
            });
          });
          return;
        }
        case Enums.ROLES.supporter: {
          [
            listenEventDict["update-order"],
            listenEventDict["allow-update"],
            listenEventDict["order-cancelled"],
            listenEventDict["request-cancel"],
            listenEventDict["reply-request-cancel"],
            listenEventDict["view-notification"],
          ].forEach((event) => {
            socket.on(event, (data) => {
              const isValid = [
                Constants.notificationType["update-order"],
                Constants.notificationType["allow-update"],
                Constants.notificationType["order-cancelled"],
                Constants.notificationType["request-cancel"],
                Constants.notificationType["reply-request-cancel"],
                Constants.notificationType["view-notification"],
              ].includes(data);

              if (!isValid) {
                return;
              }

              this.getNotifications({
                ...this.filter
              });
            });
          });
          return;
        }
        case Enums.ROLES.inventory_manager: {
          [
            listenEventDict["warning-low-stock"],
          ].forEach((event) => {
            socket.on(event, (data) => {
              const isValid = [
                Constants.notificationType["warning-low-stock"],
              ].includes(data);

              if (!isValid) {
                return;
              }

              this.getNotifications({
                ...this.filter
              });
            });
          })
          return;
        }
        default:
          return;
      }
    }
  },
  watch: {
    'filter.page': function () {
      this.getNotifications({
        ...this.filter,
      });
    }
  },
  methods: {
    async getNotifications(params) {
      try {
        if (!params) {
          params = this.filter;
        }

        this.loading = true
        const res = await NotificationService.pagination({
          ...(params && { ...params }),
          limit: params && params.limit ? params.limit : this.filter.limit,
          page: params && params.page ? params.page : this.filter.page,
        });
        this.items = res.rows;
        this.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        switch (true) {
          case Boolean(params && params.limit): {
            this.filter.limit = params.limit;
          }
          case Boolean(params && params.page): {
            this.filter.page = params.page;
          }
          case Boolean(params && params.status): {
            this.filter.status = params.status;
          }
          case Boolean(params && params.keyword): {
            this.filter.keyword = params.keyword;
          }
          case Boolean(params && params.createdFrom): {
            this.filter.createdFrom = params.createdFrom;
          }
          case Boolean(params && params.createdTo): {
            this.filter.createdTo = params.createdTo;
          }
          default:
            break;
        }

        this.totalPage = res.totalPage;
        this.total = res.count;
        this.totalUnviewed = res.totalUnviewed;
      } catch (e) {

      } finally {
        this.loading = false
      }
    },
    async handleView(notification) {
      if (!notification.status) {
        const data = await NotificationHelper.view(notification);
        if (data.message !== 'success') {
          return;
        }
        notification.status = 1;
      }
      if (notification.meta) {
        const meta = JSON.parse(notification.meta);
        window.open(meta.link || "", "_blank");
      }
    },
    type(value) {
      return NotificationHelper.type(value);
    }
  }
};
</script>
<style>
.odd {
  background-color: white !important;
}

.even {
  background-color: white !important;
  color: rgba(128, 128, 128, 0.63) !important;
}
</style>
